/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {Box, Heading, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import PageActionPlaceHolder from '../../components/page-action-placeholder'
import {RepairIcon} from '../../components/custom-icons'
import {CONTACT_US_URL} from '../../constants'
import useMultiSite from '@app/hooks/use-multi-site'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * AccountRepair is a React functional component that renders a page for the repair service section
 * of a user's account. This component provides information about the repair service and guides users
 * to contact customer service for repair requests.
 *
 * The component utilizes internationalization to display messages in different languages and includes
 * a navigation function to redirect users to the contact page when they click the "Contact Us" button.
 *
 * @component
 * @returns {JSX.Element} A JSX element representing the repair service page with a heading and a placeholder
 * for the repair service information.
 *
 * @example
 * return (
 *   <AccountRepair />
 * )
 */
const AccountRepair = () => {
    const {formatMessage} = useIntl()
    const {usePageLoadAndCookies} = useGTM()
    const {buildUrl} = useMultiSite()

    usePageLoadAndCookies('account')

    return (
        <Stack spacing={24} data-testid="account-repair-page">
            <Heading
                as="h1"
                display="inline-block"
                size="3xl"
                textTransform="uppercase"
                tabIndex="0"
            >
                <FormattedMessage defaultMessage="Repair service" id="account_repair.title" />
            </Heading>
            <Box layerStyle="accountCard" data-testid="account-repair-place-holder">
                <PageActionPlaceHolder
                    icon={<RepairIcon boxSize={8} />}
                    heading={formatMessage({
                        defaultMessage: 'Our Repair Service is getting refreshed.',
                        id: 'account_repair.heading'
                    })}
                    text={formatMessage({
                        defaultMessage:
                            'Please contact Vilebrequin customer service for repair requests.',
                        id: 'account_repair.description'
                    })}
                    buttonText={formatMessage({
                        defaultMessage: 'Contact Us',
                        id: 'account_repair.button'
                    })}
                    buttonProps={{
                        variant: 'primary',
                        as: 'a',
                        href: buildUrl(CONTACT_US_URL)
                    }}
                />
            </Box>
        </Stack>
    )
}

AccountRepair.getTemplateName = () => 'account-order-repair'

export default AccountRepair
